import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router';
import { z } from 'zod';
import { checkPasswordPolicy, PASSWORD_MAX_LENGTH } from '@fas/validation';
import { Alert, Button, Field, Input } from '@fas/ui';
import { apiRequest, errorMessage } from '@/lib/api';
import { AuthLayout } from './AuthLayout';
import { PasswordPolicyHints, usePasswordPolicy } from './PasswordPolicyHints';

/** Reads the one-time token from the URL fragment and removes it from browser history. */
function useFragmentToken(): string | null {
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token'));
  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
  }, []);
  return token;
}

export function ResetPasswordPage() {
  const token = useFragmentToken();
  const policy = usePasswordPolicy();
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const schema = z
    .object({
      newPassword: z.string().min(1, 'Choose a new password').max(PASSWORD_MAX_LENGTH),
      confirm: z.string().min(1, 'Confirm your password'),
    })
    .superRefine((v, ctx) => {
      const problems = checkPasswordPolicy(v.newPassword, policy);
      if (problems.length) ctx.addIssue({ code: 'custom', path: ['newPassword'], message: problems[0] });
      if (v.newPassword !== v.confirm) ctx.addIssue({ code: 'custom', path: ['confirm'], message: 'Passwords do not match' });
    });
  const form = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema), defaultValues: { newPassword: '', confirm: '' } });
  const password = form.watch('newPassword');

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const { data } = await apiRequest<{ message: string }>('POST', '/auth/reset-password', {
        body: { token, newPassword: values.newPassword },
        auth: false,
      });
      setDone(data.message);
    } catch (err) {
      setError(errorMessage(err));
    }
  });

  if (!token) {
    return (
      <AuthLayout title="Set your password">
        <Alert tone="warning" title="Link missing or incomplete">
          Open the link from your e-mail again, or request a new one.
        </Alert>
        <Link to="/forgot-password" className="mt-4 block text-center text-sm font-medium text-blue-700 hover:underline">
          Request a new link
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Set your password" subtitle="Choose a strong password you do not use elsewhere">
      {done ? (
        <div className="space-y-4">
          <Alert tone="success">{done}</Alert>
          <Button asChild className="w-full">
            <Link to="/login">Go to sign in</Link>
          </Button>
        </div>
      ) : (
        <form noValidate onSubmit={onSubmit} className="space-y-4">
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <Field id="reset-password" label="New password" error={form.formState.errors.newPassword?.message} required>
            {(a11y) => <Input {...a11y} type="password" autoComplete="new-password" autoFocus {...form.register('newPassword')} />}
          </Field>
          <PasswordPolicyHints password={password} policy={policy} />
          <Field id="reset-confirm" label="Confirm new password" error={form.formState.errors.confirm?.message} required>
            {(a11y) => <Input {...a11y} type="password" autoComplete="new-password" {...form.register('confirm')} />}
          </Field>
          <Button type="submit" className="w-full" loading={form.formState.isSubmitting}>
            Save password
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
